// src/pages/property-types/PropertyTypeEditPage.tsx
import {useState, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {ArrowLeft, Save} from "lucide-react";
import {
  PageHeader,
  LoadingSpinner,
  EmptyState,
  FormField,
  PropertyTypeIconUpload,
} from "../../components/common";
import {Button, Card} from "../../components/ui";
import {usePropertyType, usePropertyTypes} from "../../hooks/usePropertyTypes";
import {useToast} from "../../hooks/common/useToast";
import {ROUTES} from "../../utils/constants";
import type {UpdatePropertyTypeDto} from "../../types/property-type";

const PropertyTypeEditPage: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {success, error} = useToast();

  const propertyTypeId = id ? parseInt(id) : undefined;
  const {propertyType, isLoading: loadingPropertyType} =
    usePropertyType(propertyTypeId);
  const {handleUpdate, isUpdating} = usePropertyTypes();

  const [formData, setFormData] = useState<UpdatePropertyTypeDto>({
    nameEng: "",
    nameMm: "",
    icon: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load property type data
  useEffect(() => {
    if (propertyType) {
      setFormData({
        nameEng: propertyType.nameEng,
        nameMm: propertyType.nameMm,
        icon: propertyType.icon || "",
      });
    }
  }, [propertyType]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nameEng?.trim()) {
      newErrors.nameEng = "English name is required";
    } else if (formData.nameEng.length > 100) {
      newErrors.nameEng = "English name must be less than 100 characters";
    }

    if (!formData.nameMm?.trim()) {
      newErrors.nameMm = "Myanmar name is required";
    } else if (formData.nameMm.length > 100) {
      newErrors.nameMm = "Myanmar name must be less than 100 characters";
    }

    // Icon validation is now handled by the upload component

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!propertyTypeId) return;

    if (!validateForm()) {
      error("Validation Error", "Please fix the errors and try again");
      return;
    }

    const success = await handleUpdate(propertyTypeId, {
      nameEng: formData.nameEng?.trim(),
      nameMm: formData.nameMm?.trim(),
      icon: formData.icon?.trim() || undefined,
    } as UpdatePropertyTypeDto);

    if (success) {
      navigate(ROUTES.PROPERTY_TYPES);
    }
  };

  // Handle field changes
  const handleFieldChange = (
    field: keyof UpdatePropertyTypeDto,
    value: string
  ) => {
    setFormData((prev) => ({...prev, [field]: value}));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: ""}));
    }
  };

  // Loading state
  if (loadingPropertyType) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Not found state
  if (!propertyType) {
    return (
      <EmptyState
        title="Property Type Not Found"
        description="The property type you're looking for doesn't exist or has been removed."
        action={{
          label: "Back to Property Types",
          onClick: () => navigate(ROUTES.PROPERTY_TYPES),
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={`Edit ${propertyType.nameEng}`}
        breadcrumbs={[
          {label: "Dashboard", path: "/dashboard"},
          {label: "Property Types", path: ROUTES.PROPERTY_TYPES},
          {label: "Edit"},
        ]}
      >
        <Button
          variant="secondary"
          onClick={() => navigate(ROUTES.PROPERTY_TYPES)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Property Types
        </Button>
      </PageHeader>

      {/* Form */}
      <Card className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                name="nameEng"
                label="Name (English)"
                type="text"
                value={formData.nameEng || ""}
                onChange={(value) => handleFieldChange("nameEng", value)}
                error={errors.nameEng || ""}
                required
                placeholder="Enter property type name in English"
                disabled={isUpdating}
              />

              <FormField
                name="nameMm"
                label="Name (Myanmar)"
                type="text"
                value={formData.nameMm || ""}
                onChange={(value) => handleFieldChange("nameMm", value)}
                error={errors.nameMm || ""}
                required
                placeholder="Enter property type name in Myanmar"
                disabled={isUpdating}
              />
            </div>
          </div>

          {/* Icon Upload */}
          <PropertyTypeIconUpload
            propertyTypeId={propertyTypeId}
            currentIcon={propertyType?.icon}
            onIconChange={(iconUrl) => {
              // For edit mode, the PropertyTypeIconUpload handles S3 upload directly
              setFormData(prev => ({ ...prev, icon: iconUrl || "" }));
            }}
            disabled={isUpdating}
          />

          {/* Property Count Info */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center">
              <div className="text-blue-600 dark:text-blue-400">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  This property type is currently used by{" "}
                  <span className="font-medium">
                    {propertyType.propertyCount}
                  </span>{" "}
                  {propertyType.propertyCount === 1 ? "property" : "properties"}
                  .
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-dark-600">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(ROUTES.PROPERTY_TYPES)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isUpdating} disabled={isUpdating}>
              <Save className="w-4 h-4 mr-2" />
              Update Property Type
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default PropertyTypeEditPage;
